import { useMutation, useQueryClient } from 'react-query';

import {
  BookmarkLookupItem,
  BookmarkableType,
  bookmarkStatusQueryKey,
  destroyBookmark,
} from '@utils/backend-api/bookmarks';

interface UseBookmarkDestroyParams {
  userId: number | string | null | undefined;
  bookmarkableType: BookmarkableType;
  contentId: number;
}

interface MutationContext {
  previousStatus?: BookmarkLookupItem;
}

export default function useBookmarkDestroy({
  userId,
  bookmarkableType,
  contentId,
}: UseBookmarkDestroyParams) {
  const queryClient = useQueryClient();
  const queryKey = bookmarkStatusQueryKey(userId, bookmarkableType, contentId);

  const { mutate, mutateAsync, isLoading, isError } = useMutation(
    async () => destroyBookmark({ type: bookmarkableType, id: contentId }),
    {
      onMutate: async (): Promise<MutationContext> => {
        await queryClient.cancelQueries(queryKey);
        const previousStatus = queryClient.getQueryData<BookmarkLookupItem>(queryKey);

        queryClient.setQueryData<BookmarkLookupItem>(queryKey, {
          type: bookmarkableType,
          id: contentId,
          bookmarked: false,
          bookmarked_at: null,
        });

        return { previousStatus };
      },
      onSuccess: () => {
        queryClient.setQueryData<BookmarkLookupItem>(queryKey, {
          type: bookmarkableType,
          id: contentId,
          bookmarked: false,
          bookmarked_at: null,
        });
      },
      onError: (_error, _variables, context) => {
        if (context?.previousStatus) {
          queryClient.setQueryData(queryKey, context.previousStatus);
        }
      },
    }
  );

  return {
    destroyBookmark: mutate,
    destroyBookmarkAsync: mutateAsync,
    isDestroyingBookmark: isLoading,
    isDestroyBookmarkError: isError,
  };
}
